import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { useWorkspacesStore } from "stores/workspaces";
import api from '../services/api'
import merge from 'deepmerge'

export const useFormsStore = defineStore('forms', () => {
  const workspacesStore = useWorkspacesStore()
  const forms = ref([])
  const active = ref(getEmptyForm())
  const edited = ref(getEmptyForm())
  const loadingActive = ref(false)
  const loadingForms = ref(false)
  const checklistVisible = ref(true)
  const initialized = ref(false)
  const creatingForm = ref(false)

  const saved = ref(true)
  const saving = ref(false)
  const saveWaitTimeout = ref(1)

  function $reset() {
    forms.value = []
    active.value = getEmptyForm()
    edited.value = getEmptyForm()
    loadingActive.value = false
    loadingForms.value = false
    checklistVisible.value = true
    initialized.value = false
    creatingForm.value = false
    saved.value = true
    saving.value = false
    saveWaitTimeout.value = 1
  }

  async function endLoading() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingActive.value = false
      console.log('loadingActive=false')
    }, 10)
  }

  async function endLoadingForms() {
    setTimeout(() => {
      //doing this in timeout to make sure that the watchers of previous lines finished running
      //otherwise auto-save will be triggered immediately after loading a form
      loadingForms.value = false
    }, 10)
  }

  async function update() {
    loadingActive.value = true // guard against triggering autosave due to response from save
    console.log('updating form. loadingActive=true')
    const updated = await api.updateForm(workspacesStore.active.id, edited.value)
    if (!updated) {
      await endLoading.call(this)
      return
    }
    active.value = updated
    edited.value = merge({}, updated)
    await endLoading.call(this)
  }

  /**
   * Sets current and active form to the value provided. Useful after an API call that was made outside of the store, that returns the updated form, and we want to update the store
   * @param {Form} form
   * @returns {Promise<void>}
   */
  async function setActiveForm(form) {
    loadingActive.value = true // guard against triggering autosave due to response from save
    active.value = form
    edited.value = merge({}, form)
    await endLoading.call(this)
  }

  async function loadForm(workspaceId, formId) {
    loadingActive.value = true
    const form = await api.getForm(workspaceId, formId)
    active.value = form
    edited.value = merge({}, form)
    await endLoading.call(this)
  }

  async function reloadEditedForm() {
    const form = await api.getForm(workspacesStore.active.id, edited.value.id)
    active.value = form
    edited.value = merge({}, form)
  }

  async function loadForms(force = false) {
    if (loadingForms.value && initialized.value) {
      return
    }
    if (!force && forms.value.length) {
      return
    }
    loadingForms.value = true
    if (workspacesStore.active?.id) {
      initialized.value = true
      const loadedForms = await api.listForms(workspacesStore.active.id)
      forms.value.splice(0, forms.value.length, ...loadedForms);
      await endLoadingForms.call(this)
    }
  }

  watch ( () => workspacesStore.active?.id,
    async wsid => {
      if (wsid) {
        await loadForms(true)
      }
    },
    { immediate: true }
  )

  /**
   * @param {string} name
   */
  async function createForm (name) {
    creatingForm.value = true
    const form = await api.createForm(workspacesStore.active.id, { name })
    active.value = form
    edited.value = merge({}, form)
    creatingForm.value = false
    return form
  }

  return {
    $reset,
    forms,
    active,
    checklistVisible,
    edited,
    saved,
    saving,
    saveWaitTimeout,
    loadingForms,
    loadingActive,
    creatingForm,
    // Actions
    update,
    createForm,
    loadForms,
    loadForm,
    reloadEditedForm,
    setActiveForm
  }
})



function getEmptyForm() {
  return {
    // "name": "My Form",
    // "emailField": "email",
    // "redirectUrl": "",
    // "addDataToRedirect": false,
    // "dataRetention": 30,
    // "filesRetention": 30,
    // "referrer": "",
    // "honeypotField": "xx_password",
    // "recaptchaEnabled": false,
    // "recaptchaKey": "",
    // "recaptchaAction": "",
    // "recaptchaThreshold": 0.5,
    // "zapierKey": "",
    // "formKey": "",
    // "draftModel": null,
    "model": {
      // "schema": null,
      "options": {}
    },
    "emailNotification": {
      // "enabled": false,
      "to": [],
      // "fromName": "",
      // "fromEmail": "",
      // "replyToVisitor": false,
      // "hideViewInDashboard": false,
      // "replyTo": "",
      // "subject": "New form submission from {{formName}}",
      // "filesLinkExpiry": 30
    },
    "validation": {
      // "enabled": false,
      "json": {}
    },
    "autoResponse": {
      // "enabled": false,
      // "fromName": "",
      // "subject": "",
      "template": "",
      // "replyTo": "",
      "templateJson": {},
      // "templateIsFullHtml": false
    },
    "webhook": {
      // "enabled": false,
      // "url": "",
      // "webhookBodyParams": "",
      // "webhookHeader": "",
      // "webhookBodyOverride": "",
      // "secret": ""
    },
    "spamFilters": {
      "countriesFilter": {
        // "enabled": false,
        // "whiteList": false,
        "countries": []
      },
      "contentFilter": {
        // "enabled": false,
        "filters": []
      },
      "bypass": {
        // "enabled": false,
        // "secret": ""
      }
    },
    "doubleOptIn": {
      // "enabled": false,
      "email": {
        // "fromName": "",
        // "subject": "",
        // "template": "",
        // "replyTo": ""
      }
    },
    "noBounce": {
      // "enabled": false
    },
    "fileUpload": {
      "enabled": false
    }
  }
}
/**
 * @typedef Form
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string} accountId
 * @property {Object} emailNotification
 * @property {boolean} emailNotification.hideViewInDashboard
 * @property {number} emailNotification.filesLinkExpiry
 * @property {string} emailNotification.subject
 * @property {string} emailNotification.fromName
 * @property {boolean} emailNotification.replyToVisitor
 * @property {string} emailNotification.replyTo
 * @property {Array<string>} emailNotification.to
 * @property {boolean} emailNotification.enabled
 * @property {string} redirectUrl
 * @property {Object} webhook
 * @property {string} webhook.webhookBodyParams
 * @property {string} webhook.webhookHeader
 * @property {string} webhook.webhookBodyOverride
 * @property {string} webhook.secret
 * @property {boolean} webhook.enabled
 * @property {string} webhook.url
 * @property {string} formKey
 * @property {string} publishedUrl
 * @property {Object} spamFilters
 * @property {Object} spamFilters.bypass
 * @property {string} spamFilters.bypass.secret
 * @property {boolean} spamFilters.bypass.enabled
 * @property {Object} spamFilters.contentFilter
 * @property {Array<string>} spamFilters.contentFilters.filters
 * @property {boolean} spamFilters.contentFilters.enabled
 * @property {Object} spamFilters.countriesFilter
 * @property {boolean} spamFilters.countriesFilter.whiteList
 * @property {Array<string>} spamFilters.countriesFilter.countries
 * @property {boolean} spamFilters.countriesFilter.enabled
 * @property {string} emailField
 * @property {Object} autoResponse
 * @property {string} autoResponse.template
 * @property {string} autoResponse.subject
 * @property {string} autoResponse.fromName
 * @property {string} autoResponse.fromEmail
 * @property {string} autoResponse.replyTo
 * @property {boolean} autoResponse.enabled
 * @property {string} zapierKey
 * @property {string} honeypotField
 * @property {boolean} recaptchaEnabled
 * @property {string} referrer
 * @property {number} dataRetention
 * @property {Any} draftModel
 * @property {string} recaptchaAction - deprecated
 * @property {number} recaptchaThreshold - deprecated
 * @property {Object} doubleOptIn
 * @property {boolean} doubleOptIn.enabled
 * @property {Object} doubleOptIn.email
 * @property {string} doubleOptIn.email.template
 * @property {string} doubleOptIn.email.subject
 * @property {string} doubleOptIn.email.fromName
 * @property {string} doubleOptIn.email.replyTo
 * @property {string} doubleOptIn.name
 * @property {Object} doubleOptIn.model
 * @property {Any} doubleOptIn.model.schema
 * @property {Any} doubleOptIn.model.options
 * @property {boolean} addDataToRedirect
 * @property {number} filesRetention
 * @property {Object} validation
 * @property {Object} validation.json
 * @property {boolean} validation.enabled
 * @property {Object} noBounce
 * @property {boolean} noBounce.enabled
 * @property {Object} fileUpload
 * @property {boolean} fileUpload.enabled
 * @property {boolean} branding - whether the form descriptor itself is using branding features (css / removeBranding)
 */
